<template>
  <calm-page extra-class="profile" :title="$t('profile.profile')">
    <form class="profile__form">
      <!-- ─────── Identity summary (name / email / role / branch + avatar) ─────── -->
      <div class="profile__summary">
        <calm-avatar-upload
          class="profile__avatar"
          size="lg"
          :name="summaryName"
          v-model="avatarPreview"
          @change="onPhotoChange"
          @remove="onPhotoRemove"
        />
        <div class="profile__summary-info">
          <div class="profile__summary-name">{{ summaryName }}</div>
          <div class="profile__summary-line" v-if="getUser.Username || profile.Email">
            {{ getUser.Username || profile.Email }}
          </div>
          <div class="profile__summary-tags">
            <span class="profile__tag profile__tag--role">{{ roleLabel }}</span>
            <span class="profile__tag" v-if="getUser.BranchName">{{ getUser.BranchName }}</span>
          </div>
        </div>
      </div>

      <!-- ─────── 1. ข้อมูลส่วนตัว (Personal) ─────── -->
      <calm-form-section :num="1" :title="$t('profile.name')">
        <div class="calm__form-grid calm__form-grid--3">
          <calm-form-row :label="$t('profile.name')">
            <base-input placeholder="" v-model="profile.FName"></base-input>
          </calm-form-row>

          <calm-form-row :label="$t('profile.lname')">
            <base-input placeholder="" v-model="profile.LName"></base-input>
          </calm-form-row>

          <calm-form-row :label="$t('birthday')">
            <base-input>
              <flat-picker
                slot-scope="{ focus, blur }"
                @on-open="focus"
                @on-close="blur"
                :config="{ allowInput: true }"
                class="form-control datepicker"
                v-model="profile.BirthDay"
              >
              </flat-picker>
            </base-input>
          </calm-form-row>

          <calm-form-row :label="$t('lmg.email')">
            <base-input v-model="profile.Email"> </base-input>
          </calm-form-row>

          <calm-form-row :label="$t('lmg.tel')">
            <base-input v-model="profile.Tel"> </base-input>
          </calm-form-row>
        </div>
      </calm-form-section>

      <!-- ─────── 2. ข้อมูลตัวแทน/ภาษี (Agent / tax) ─────── -->
      <calm-form-section :num="2" :title="$t('lmg.agent_code')">
        <div class="calm__form-grid calm__form-grid--3">
          <calm-form-row :label="$t('lmg.agent_code')">
            <base-input v-model="profile.AgentCode"> </base-input>
          </calm-form-row>

          <calm-form-row :label="$t('lmg.taxid')">
            <base-input v-model="profile.TaxID"> </base-input>
          </calm-form-row>

          <calm-form-row :label="$t('lmg.with_holding_tax')">
            <base-input v-model="profile.WithholdingTax"> </base-input>
          </calm-form-row>
        </div>
      </calm-form-section>

      <!-- ─────── 3. ที่อยู่ (Address) ─────── -->
      <calm-form-section :num="3" :title="$t('lmg.address')">
        <div class="calm__form-grid calm__form-grid--3">
          <calm-form-row class="profile__col-span-3" :label="$t('lmg.address')">
            <base-input v-model="profile.Address"> </base-input>
          </calm-form-row>

          <calm-form-row :label="$t('lmg.city')">
            <base-input v-model="profile.City"> </base-input>
          </calm-form-row>

          <calm-form-row :label="$t('lmg.province')">
            <base-input v-model="profile.Province"> </base-input>
          </calm-form-row>

          <calm-form-row :label="$t('lmg.postcode')">
            <base-input v-model="profile.Postcode"> </base-input>
          </calm-form-row>
        </div>
      </calm-form-section>

      <div class="calm__modal__actions profile__actions">
        <calm-button variant="primary" @click="handleUpdate()">
          {{ $t("save") }}
        </calm-button>
      </div>
    </form>
  </calm-page>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import clientPaginationMixin from "./PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import numbers from "./numbers";
import { mapGetters, mapActions } from "vuex";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import CalmAvatarUpload from "@/components/lmg/CalmAvatarUpload.vue";

export default {
  name: "CreateNumber",
  components: {
    flatPicker,
    CalmAvatarUpload,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn
  },
  computed: {
    ...mapGetters({
      getUser: "Auth/getUser",
    }),
    summaryName() {
      const fromProfile = [this.profile.FName, this.profile.LName]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (fromProfile) return fromProfile;
      const u = this.getUser || {};
      return [u.FName, u.LName].filter(Boolean).join(" ").trim();
    },
    roleLabel() {
      const t = this.getUser ? this.getUser.Type : 0;
      if (t == 99) return this.$t("profile.role_super_admin");
      if (t == 3) return this.$t("profile.role_branch_admin");
      if (t == 2) return this.$t("profile.role_agent");
      return this.$t("profile.role_user");
    }
  },
  data() {
    return {
      profile: {},
      avatarPreview: "", // data-URI shown in the avatar
      photoFile: null, // newly selected File (null = no change)
      photoRemoved: false // user explicitly removed the photo
    };
  },
  async mounted() {
    try {
      const resp = await this.getProfile();
      const list = resp && resp.data && resp.data.Data;
      this.profile = list && list.length ? list[0] : {};

      // Load existing photo (behind JWT) as base64 data-URI for preview.
      if (this.profile && this.profile.ID && this.profile.Photo) {
        try {
          const img = await this.getImage(this.profile.ID);
          this.avatarPreview = img && img.data ? img.data : "";
        } catch (e) {
          this.avatarPreview = "";
        }
      }
    } catch (err) {
      // Never blank the page on a fetch failure — identity still comes from getUser.
      this.profile = {};
    }
  },

  methods: {
    ...mapActions({
      setUser: "Auth/setUser",
      changeProfile: "Auth/changeProfile",
      changeProfilePhoto: "Auth/changeProfilePhoto",
      changePassword: "Auth/changePassword",
      getProfile: "Auth/getProfile",
      getImage: "Person/getImage"
    }),
    onPhotoChange(file) {
      this.photoFile = file || null;
      this.photoRemoved = file === null;
    },
    onPhotoRemove() {
      this.photoFile = null;
      this.photoRemoved = true;
    },
    profileFields() {
      return {
        fname: this.profile.FName,
        lname: this.profile.LName,
        birthDay: this.profile.BirthDay,
        agentCode: this.profile.AgentCode,
        withholdingTax: this.profile.WithholdingTax,
        taxID: this.profile.TaxID,
        tel: this.profile.Tel,
        email: this.profile.Email,
        address: this.profile.Address,
        city: this.profile.City,
        province: this.profile.Province,
        postcode: this.profile.Postcode
      };
    },
    handleUpdate() {
      let request;
      const removing = this.photoRemoved && !this.photoFile;
      if (this.photoFile || removing) {
        // Photo changed or removed -> multipart with text fields + photo/flag.
        const fd = new FormData();
        const fields = this.profileFields();
        Object.keys(fields).forEach(k => {
          fd.append(k, fields[k] == null ? "" : fields[k]);
        });
        if (this.photoFile) {
          fd.append("photo", this.photoFile);
        } else {
          // Explicit removal -> tell the backend to clear the stored photo.
          fd.append("removePhoto", "1");
        }
        request = this.changeProfilePhoto(fd);
      } else {
        // No new photo -> existing text-only path (preserves stored photo).
        request = this.changeProfile(this.profileFields());
      }

      request
        .then(resp => {
          if (removing) this.avatarPreview = "";
          this.photoFile = null;
          this.photoRemoved = false;
          swal
            .fire({
              title: this.$t("profile.change_profile_success"),
              text: ``,
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false
            })
            .then(result => {});
        })
        .catch(err => {
          swal.fire({
            title: this.$t("errors.profile.change_profile_failed"),
            text: this.$t(err.response.data.Msg),
            icon: "error",
            confirmButtonClass: "btn btn-danger btn-fill",
            buttonsStyling: false
          });
        });
    }
  }
};
</script>

<style scoped>
/* Profile (/profile/me) — Calm skin. Everything namespaced under .profile so
   nothing leaks to other pages. The form keeps its base-input wrappers and
   v-model bindings, re-laid-out via calm-form-section + calm-form-row. */

/* Grid spans for fields that should occupy more than one column in the
   3-column form grid (mirrors the legacy full-width address field). */
.profile__col-span-3 { grid-column: span 3; }
@media (max-width: 720px) {
  .profile__col-span-3 { grid-column: span 1; }
}

/* Action row pinned to the bottom of the form. */
.profile__actions { margin-top: 20px; }

/* Identity summary header (avatar + name/email/role/branch). */
.profile__summary {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 18px 20px;
  margin-bottom: 22px;
  background: var(--c-surface, #fff);
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 14px;
}
.profile__avatar { flex: none; }
.profile__summary-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.profile__summary-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--c-text, #2c3e50);
}
.profile__summary-line {
  font-size: 13px;
  color: var(--c-text-mute, #8a98ab);
}
.profile__summary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 2px;
}
.profile__tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: var(--c-bg, #f4f6fa);
  color: var(--c-text-soft, #5a6b80);
  border: 1px solid var(--c-border, #e4e9f1);
}
.profile__tag--role {
  background: var(--c-blue-soft);
  color: var(--c-blue-deep);
  border-color: transparent;
}
@media (max-width: 600px) {
  .profile__summary {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }
}

/* Drop the base-input's own bottom margin — calm-form-row owns the spacing. */
.profile__form >>> .form-group {
  margin-bottom: 0;
}
/* base-input renders its own <label>; we use calm-form-row's label instead,
   so hide the inner one to avoid a doubled label. */
.profile__form >>> .form-group > label {
  display: none;
}
</style>
